/**
 * Users Module Component
 *
 * Main component for the Users module providing comprehensive user management.
 * Features:
 * - User listing with infinite scroll
 * - User CRUD operations
 * - Status and role management
 * - Column-based filtering
 *
 * @module Users
 */
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useSearchParams } from "react-router-dom";
import useThrottledScroll from "../../hooks/useThrottledScroll";
import { getAllCompanies } from "../../services/companyService";
import { extractCompanyNames } from "../../helpers/companyHelpers";
import { getAllRoles } from "../../services/roleService";
import { useUserManagement } from "./hooks";
import { hasPermission, canViewAllCompanies } from "../../utils/roleHelpers";
import {
  SCROLL_THRESHOLD_PIXELS,
  SCROLL_THROTTLE_DELAY,
} from "./constants/userConstants";

// Components
import UserTable from "./components/Table/UserTable";
import UserFormModal from "./components/Modal/UserFormModal";

// Styles
import "./Users.scss";

/**
 * Users Module Main Component
 * Orchestrates user management functionality
 *
 * @param {Object} userInfo - Current logged-in user information
 * @param {boolean} isSuperAdmin - Whether current user is Super Admin
 */
const Users = ({ userInfo, isSuperAdmin = false }) => {
  // Check if user can view users module
  const canViewUsers = useMemo(
    () => hasPermission(userInfo, 'users', 'view'),
    [userInfo]
  );

  // Permission checks for user actions
  const canCreateUser = useMemo(
    () => hasPermission(userInfo, 'users', 'create'),
    [userInfo]
  );

  const canUpdateUser = useMemo(
    () => hasPermission(userInfo, 'users', 'update'),
    [userInfo]
  );

  const canDeleteUser = useMemo(
    () => hasPermission(userInfo, 'users', 'delete'),
    [userInfo]
  );

  const canResetPassword = useMemo(
    () => hasPermission(userInfo, 'users', 'reset_password'),
    [userInfo]
  );

  // Check if user can view all companies for users module
  const canViewAllCompaniesInUsers = useMemo(
    () =>
      canViewAllCompanies(userInfo, 'dashboard') ||
      hasPermission(userInfo, 'users', 'view_all_companies'),
    [userInfo]
  );

  // User is NOT restricted if they are Super Admin or have view_all_companies permission
  const restrictToOwnCompany = useMemo(
    () => !(isSuperAdmin || canViewAllCompaniesInUsers),
    [isSuperAdmin, canViewAllCompaniesInUsers]
  );

  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

  // User management hook - handles all user CRUD operations
  const {
    users,
    loading,
    isLoadingMore,
    hasMore,
    totalCount,
    filters,
    loadUsers,
    loadMoreUsers,
    refreshUsers,
    updateFilters,
    toggleUserStatus,
    changeUserRole,
    removeUser,
  } = useUserManagement(userInfo, restrictToOwnCompany, sortOrder);

  // Ref to track initial load (prevents double API call in React Strict Mode)
  const hasInitiallyLoaded = useRef(false);

  // Get URL search params to check for company filter
  const [searchParams] = useSearchParams();
  const companyFromUrl = searchParams.get('company');
  const urlFilterApplied = useRef(false);

  /**
   * Apply company filter from URL on mount or when URL changes
   */
  useEffect(() => {
    if (companyFromUrl && !urlFilterApplied.current) {
      urlFilterApplied.current = true;
      updateFilters({ company: companyFromUrl });
      loadUsers(true, { company: companyFromUrl });
    }
  }, [companyFromUrl, updateFilters, loadUsers]);

  /**
   * Loads companies from companies table via API
   * Fetches all companies and extracts company names
   */
  const loadCompanies = useCallback(async () => {
    try {
      setCompaniesLoading(true);
      // Fetch all companies without pagination
      const result = await getAllCompanies({}, null);
      
      if (result && result.companies && Array.isArray(result.companies)) {
        // Extract company names and sort alphabetically
        const companiesList = extractCompanyNames(result.companies);
        const sortedCompanies = companiesList.sort((a, b) => 
          a.localeCompare(b, undefined, { sensitivity: 'base' })
        );
        setCompanies(sortedCompanies);
      } else {
        setCompanies([]);
      }
    } catch (error) {
      console.error("Error loading companies:", error);
      setCompanies([]);
    } finally {
      setCompaniesLoading(false);
    }
  }, []);

  /**
   * Loads roles from backend API
   * Backend already returns roles sorted by 'order' field (ASC)
   */
  const loadRoles = useCallback(async () => {
    try {
      const response = await getAllRoles();
      if (response && response.roles) {
        // Roles are already sorted by backend (ORDER BY `order` ASC)
        setRoles(response.roles);
      }
    } catch (error) {
      console.error("Error loading roles:", error);
    }
  }, []);

  /**
   * Handles sort order change for user name
   */
  const handleSortChange = useCallback(() => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  }, []);

  /**
   * Reload users when sort order changes
   */
  useEffect(() => {
    if (hasInitiallyLoaded.current) {
      loadUsers(true);
    }
  }, [sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Loads more users (for infinite scroll)
   * @param {boolean} shouldScroll - If true, scrolls to bottom after loading (only for button click)
   */
  const handleLoadMoreUsers = useCallback(
    async (shouldScroll = false) => {
      if (!isLoadingMore && hasMore && !loading) {
        await loadMoreUsers(shouldScroll);
      }
    },
    [isLoadingMore, hasMore, loading, loadMoreUsers]
  );

  /**
   * Detects when user scrolls near bottom of page
   * Triggers loading more users for infinite scroll
   * Does NOT scroll - maintains current scroll position (Table.jsx pattern)
   */
  const handleScroll = useCallback(() => {
    if (isLoadingMore || !hasMore || loading) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = window.innerHeight;

    // Load more when user is near bottom (defined by SCROLL_THRESHOLD_PIXELS)
    // Pass false to shouldScroll - don't scroll, maintain position
    if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PIXELS) {
      handleLoadMoreUsers(false);
    }
  }, [isLoadingMore, hasMore, loading, handleLoadMoreUsers]);

  // Use throttled scroll handler to improve performance
  const throttledScroll = useThrottledScroll(
    handleScroll,
    SCROLL_THROTTLE_DELAY
  );

  /**
   * Opens modal for creating a new user
   * Checks permission before allowing action
   */
  const handleCreateUser = useCallback(() => {
    if (!canCreateUser) {
      console.warn("User does not have permission to create users");
      return;
    }
    setEditingUser(null);
    setModalVisible(true);
  }, [canCreateUser]);

  /**
   * Opens modal for editing an existing user
   * Checks permission before allowing action
   * @param {Object} user - User object to edit
   */
  const handleEditUser = useCallback((user) => {
    if (!canUpdateUser) {
      console.warn("User does not have permission to update users");
      return;
    }
    setEditingUser(user);
    setModalVisible(true);
  }, [canUpdateUser]);

  /**
   * Closes the user form modal
   */
  const handleModalClose = useCallback(() => {
    setModalVisible(false);
    setEditingUser(null);
  }, []);

  /**
   * Handles successful user creation or update
   * Refreshes user list
   */
  const handleModalSuccess = useCallback(() => {
    refreshUsers();
  }, [refreshUsers]);

  /**
   * Handles permanent user deletion from Auth0 and database
   * Checks permission before allowing action
   * @param {number} userId - ID of user to delete
   */
  const handleDeleteUser = useCallback(
    async (userId) => {
      if (!canDeleteUser) {
        console.warn("User does not have permission to delete users");
        return;
      }
      await removeUser(userId);
    },
    [removeUser, canDeleteUser]
  );


  /**
   * Handles user status toggle (activate/deactivate)
   * Checks permission before allowing action
   * @param {string} userEmail - Email of user
   * @param {boolean} newStatus - New status value
   */
  const handleToggleStatus = useCallback(
    async (userEmail, newStatus) => {
      if (!canUpdateUser) {
        console.warn("User does not have permission to update user status");
        return;
      }
      await toggleUserStatus(userEmail, newStatus);
    },
    [toggleUserStatus, canUpdateUser]
  );

  /**
   * Handles user role change
   * Checks permission before allowing action
   * @param {string} userEmail - Email of user
   * @param {number} newRoleId - New role ID
   */
  const handleChangeRole = useCallback(
    async (userEmail, newRoleId) => {
      if (!canUpdateUser) {
        console.warn("User does not have permission to update user roles");
        return;
      }
      await changeUserRole(userEmail, newRoleId, roles);
    },
    [changeUserRole, roles, canUpdateUser]
  );

  /**
   * Initial data load on component mount
   * Loads companies and roles
   */
  useEffect(() => {
    loadCompanies();
    loadRoles();
  }, []);

  /**
   * Load users on initial mount
   * Resets to page 1
   * Uses ref to prevent double API call in React Strict Mode (development)
   * Skip if URL filter was applied (it handles its own loadUsers call)
   */
  useEffect(() => {
    if (!hasInitiallyLoaded.current) {
      hasInitiallyLoaded.current = true;
      // Only load if URL filter wasn't applied
      if (!companyFromUrl || urlFilterApplied.current) {
        loadUsers(true);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Set up scroll listener for infinite scroll
   * Cleans up listener on unmount
   */
  useEffect(() => {
    window.addEventListener("scroll", throttledScroll);
    return () => window.removeEventListener("scroll", throttledScroll);
  }, [throttledScroll]);

  // If user doesn't have view permission, show no access message
  // (Note: Route-level access is handled in Dashboard.jsx, this is defense in depth)
  if (!canViewUsers) {
    return (
      <div className="users-module" style={{ padding: '40px', textAlign: 'center' }}>
        <h3>No Access</h3>
        <p>You don't have permission to view the Users module. Please contact your administrator.</p>
      </div>
    );
  }

  return (
    <div className="users-module">
      {/* Users Table with Infinite Scroll and Column Filters */}
      <UserTable
        currentUser={userInfo}
        users={users}
        loading={loading}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        totalUsers={totalCount}
        activeFilters={filters}
        allCompanies={restrictToOwnCompany ? [userInfo?.company].filter(Boolean) : companies}
        companiesLoading={companiesLoading}
        allRoles={roles}
        isSuperAdmin={isSuperAdmin}
        permissions={{
          create: canCreateUser,
          update: canUpdateUser,
          delete: canDeleteUser,
          reset_password: canResetPassword,
        }}
        onLoadMore={(shouldScroll) => handleLoadMoreUsers(shouldScroll)}
        onEditUser={handleEditUser}
        onDeleteUser={handleDeleteUser}
        onToggleStatus={handleToggleStatus}
        onChangeRole={handleChangeRole}
        onRefresh={refreshUsers}
        onCreateUser={handleCreateUser}
        onFilterChange={updateFilters}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
      />

      {/* User Form Modal for Create/Edit Operations */}
      <UserFormModal
        visible={modalVisible}
        editingUser={editingUser}
        companies={restrictToOwnCompany ? [userInfo?.company].filter(Boolean) : companies}
        companiesLoading={companiesLoading}
        roles={roles}
        userInfo={userInfo}
        canUpdate={canUpdateUser}
        canCreate={canCreateUser}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default Users;
